"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Shield, Users, Search, X, UserPlus, Info, Check, MapPin } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { useWorkspace } from "@/lib/context/WorkspaceContext";

interface Posting {
    id: string;
    gig_type_id: string;
    gig_type_name: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    opens_at: string | null;
    closes_at: string | null;
    count_needed: number;
    filled_count: number;
    status: string;
    rejection_reason?: string | null;
    geographic_scope: string | null;
    description: string | null;
    terms_version: number;
    created_at: string | null;
}

interface Volunteer {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    profile_photo_url?: string | null;
    member_status: string;
    job_title?: string | null;
    company?: string | null;
}

interface ConsolePostingTableProps {
    postings: Posting[];
    isLoading: boolean;
    onActionSuccess?: () => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function getCategoryName(name: string): string {
    const n = name.toLowerCase();
    if (n.includes("webinar") || n.includes("rsi") || n.includes("grade") || n.includes("grading") || n.includes("exam")) return "Education";
    if (n.includes("reference") || n.includes("journal") || n.includes("cohort")) return "Membership";
    if (n.includes("challenge") || n.includes("mentor") || n.includes("alma mater")) return "Academia Outreach";
    return "Outreach";
}

const CATEGORY_STYLES: Record<string, string> = {
    "Academia Outreach": "bg-[#ffd0d6]/30 text-[#ae232d]",
    "Education":         "bg-[#cbeef2]/30 text-[#007a8a]",
    "Membership":        "bg-[#fef3c7]/60 text-[#b45309]",
    "Outreach":          "bg-slate-50 text-slate-700",
};

function getEntityStyle(type: string): string {
    const t = type.toLowerCase();
    if (t === "staff")     return "bg-rose-50 text-rose-700 border-rose-100";
    if (t === "committee") return "bg-[#003d45]/5 text-[#003d45] border-[#003d45]/10";
    if (t === "chapter")   return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/10";
    if (t === "community") return "bg-[#ae232d]/5 text-[#ae232d] border-[#ae232d]/10";
    return "bg-[#64748b]/5 text-[#64748b] border-[#64748b]/10";
}

function getStatusStyle(status: string): string {
    const s = status.toLowerCase();
    if (s === "open")                 return "bg-emerald-50 text-emerald-700 border-emerald-100";
    if (s === "pending_staff_review") return "bg-amber-50 text-amber-700 border-amber-100";
    if (s === "draft")                return "bg-gray-50 text-gray-600 border-gray-200";
    if (s === "filled")               return "bg-[#a9c4bb]/20 text-[#004d57] border-[#a9c4bb]/40";
    if (s === "completed")            return "bg-[#a9c4bb]/20 text-[#004d57] border-[#a9c4bb]/40";
    return "bg-red-50 text-red-700 border-red-100";
}

function getEligibility(p: Posting): string {
    if (p.geographic_scope && p.geographic_scope !== "Global")
        return `Members in region (${p.geographic_scope})`;
    const n = p.gig_type_name.toLowerCase();
    if (n.includes("charterholder") || n.includes("journal") || n.includes("webinar") || n.includes("ref"))
        return "Charterholders";
    if (n.includes("candidate") || n.includes("student"))
        return "Members & candidates";
    if (n.includes("region") || n.includes("pune") || n.includes("mumbai"))
        return "Members in region";
    return "Open to all";
}

// ── component ────────────────────────────────────────────────────────────────

export default function ConsolePostingTable({ postings, isLoading, onActionSuccess }: ConsolePostingTableProps) {
    const router = useRouter();
    const { user } = useWorkspace();
    const isStaffOrAdmin = user
        ? user.is_staff || ["admin", "superadmin", "staff"].includes((user.role || "").toLowerCase())
        : false;

    const canDelete = (p: Posting) => {
        if (!user) return false;
        if (isStaffOrAdmin) return true;
        return !!user.assignments?.some(
            (a) => a.entity_type.toLowerCase() === p.owning_entity_type.toLowerCase() && a.entity_id === p.owning_entity_id
        );
    };

    // Invite modal state
    const [selectedPosting, setSelectedPosting]           = useState<Posting | null>(null);
    const [searchQuery, setSearchQuery]                   = useState("");
    const [searchResults, setSearchResults]               = useState<Volunteer[]>([]);
    const [isSearching, setIsSearching]                   = useState(false);
    const [searchError, setSearchError]                   = useState<string | null>(null);
    const [invitingId, setInvitingId]                     = useState<string | null>(null);
    const [inviteSuccessMsg, setInviteSuccessMsg]         = useState<string | null>(null);
    const [inviteErrorMsg, setInviteErrorMsg]             = useState<string | null>(null);
    const [confirmingVolunteer, setConfirmingVolunteer]   = useState<Volunteer | null>(null);
    const [checkingEligibilityId, setCheckingEligibilityId] = useState<string | null>(null);
    const [deletingPosting, setDeletingPosting]           = useState<Posting | null>(null);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setIsSearching(true); setSearchError(null); setSearchResults([]);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/organizations/users/search?q=${encodeURIComponent(searchQuery.trim())}`);
            if (!res.ok) throw new Error("Failed to search volunteers.");
            const data = await res.json();
            setSearchResults(data);
            if (data.length === 0) setSearchError("No volunteers found matching your search query.");
        } catch (err: any) { setSearchError(err.message || "Failed to search volunteers."); }
        finally { setIsSearching(false); }
    };

    const handleInviteClick = (posting: Posting) => {
        setSelectedPosting(posting); setSearchQuery(""); setSearchResults([]);
        setSearchError(null); setInviteSuccessMsg(null); setInviteErrorMsg(null);
        setConfirmingVolunteer(null); setCheckingEligibilityId(null);
    };

    const handleInviteButtonClick = async (volunteer: Volunteer) => {
        if (!selectedPosting) return;
        setCheckingEligibilityId(volunteer.id); setInviteErrorMsg(null); setInviteSuccessMsg(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings/${selectedPosting.id}/check-eligibility/${volunteer.id}`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            if (!data.eligible) { setConfirmingVolunteer(volunteer); }
            else { await handleSendInvitation(volunteer); }
        } catch { await handleSendInvitation(volunteer); }
        finally { setCheckingEligibilityId(null); }
    };

    const handleSendInvitation = async (volunteer: Volunteer, bypassEligibility: boolean = false) => {
        if (!selectedPosting) return;
        setInvitingId(volunteer.id); setInviteErrorMsg(null); setInviteSuccessMsg(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings/${selectedPosting.id}/invite`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ volunteer_id: volunteer.id, bypass_eligibility: bypassEligibility }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed to send invitation."); }
            setInviteSuccessMsg(`Invitation sent to ${volunteer.first_name} ${volunteer.last_name}!`);
            if (onActionSuccess) onActionSuccess();
            setTimeout(() => setSelectedPosting(null), 2000);
        } catch (err: any) { setInviteErrorMsg(err.message || "Failed to send invitation."); }
        finally { setInvitingId(null); }
    };

    const handleDeleteConfirm = async (posting: Posting) => {
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/postings/${posting.id}`, { method: "DELETE" });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed to delete."); }
            if (onActionSuccess) onActionSuccess();
        } catch (err: any) { alert(err.message || "Failed to delete gig posting."); }
    };

    // ── loading / empty ───────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading postings...</p>
            </div>
        );
    }

    if (postings.length === 0) {
        return (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-150 shadow-sm">
                <Shield className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">No Postings Found</h3>
                <p className="text-xs text-gray-400 mt-1">There are no gig postings matching the active filter.</p>
            </div>
        );
    }

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-4">
                {postings.map((p) => {
                    const isUnlimited      = Number(p.count_needed) === 0;
                    const isFullyStaffed   = !isUnlimited && p.filled_count >= p.count_needed;
                    const spotsLeft        = isUnlimited ? 0 : Math.max(0, p.count_needed - p.filled_count);
                    const percent          = p.count_needed > 0 ? Math.min(100, Math.round((p.filled_count / p.count_needed) * 100)) : 0;
                    const categoryName     = getCategoryName(p.gig_type_name);
                    const badgeStyle       = CATEGORY_STYLES[categoryName] || "bg-gray-50 text-gray-700";
                    const eligibility      = getEligibility(p);
                    const statusLower      = p.status.toLowerCase();

                    return (
                        <div
                            key={p.id}
                            className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-6 text-left"
                        >
                            {/* Column 1: Details */}
                            <div className="space-y-1.5 flex-1 min-w-0">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h4 className="text-base font-bold text-[#003d45]">{p.gig_type_name}</h4>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[8.5px] font-bold uppercase tracking-wider ${badgeStyle}`}>
                                        {categoryName}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getStatusStyle(p.status)}`}>
                                        {p.status.replace(/_/g, " ")}
                                    </span>
                                </div>

                                <p className="text-xs text-gray-400 font-medium flex items-center flex-wrap gap-x-1.5">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getEntityStyle(p.owning_entity_type)}`}>
                                        {p.owning_entity_name || `${p.owning_entity_type}: ${p.owning_entity_id}`}
                                    </span>
                                    <span className="text-gray-300">·</span>
                                    {p.geographic_scope && p.geographic_scope !== "Global" && <MapPin className="w-3 h-3 text-gray-400" />}
                                    {eligibility}
                                </p>

                                {/* Staffing bar */}
                                <div className="space-y-1 pt-0.5 max-w-[220px]">
                                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-[#4b8892] transition-all duration-500"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Rejection feedback */}
                                {statusLower === "draft" && p.rejection_reason && (
                                    <div className="text-[10px] text-red-600 font-semibold bg-red-50 border border-red-100 rounded-xl p-2.5 max-w-sm leading-relaxed mt-1">
                                        <span className="text-[9px] font-bold text-red-700 uppercase block mb-0.5">Staff Feedback</span>
                                        {p.rejection_reason}
                                    </div>
                                )}
                            </div>

                            {/* Column 2: Staffing stat */}
                            <div className="text-center shrink-0 w-24">
                                <div className={`text-2xl font-black ${isFullyStaffed ? "text-[#4b8892]" : "text-[#003d45]"}`}>
                                    {isUnlimited ? p.filled_count : (isFullyStaffed ? p.count_needed : spotsLeft)}
                                </div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">
                                    {isUnlimited ? "Filled (Unlimited)" : (isFullyStaffed ? "Filled" : "Needed")}
                                </div>
                            </div>

                            {/* Column 3: Actions */}
                            <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                                {statusLower === "open" && (
                                    <button
                                        onClick={() => handleInviteClick(p)}
                                        className="border-2 border-[#004d57] text-[#004d57] bg-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-gray-50 transition-all cursor-pointer"
                                    >
                                        Invite
                                    </button>
                                )}
                                {(statusLower === "draft" || statusLower === "pending_staff_review" || (statusLower === "open" && isStaffOrAdmin)) && (
                                    <button
                                        onClick={() => router.push(`/dashboard/gigs/create?id=${p.id}`)}
                                        className="bg-[#004d57] text-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-[#003c44] transition-all border-none cursor-pointer shadow-sm shadow-[#004d57]/10"
                                    >
                                        Edit
                                    </button>
                                )}
                                {canDelete(p) && statusLower !== "cancelled" && (
                                    <button
                                        onClick={() => setDeletingPosting(p)}
                                        className="text-rose-500 hover:text-rose-700 font-semibold text-[11px] cursor-pointer border-none bg-transparent hover:underline"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Invite Modal ──────────────────────────────────────────────── */}
            {selectedPosting && (
                <div className="fixed inset-0 bg-[#003d45]/45 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-150 shadow-2xl rounded-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
                        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                            <div>
                                <h3 className="text-sm font-black text-[#003d45] uppercase tracking-wider flex items-center gap-2">
                                    <UserPlus className="w-4 h-4 text-[#007a8a]" /> Invite Volunteer
                                </h3>
                                <p className="text-[10px] text-gray-400 mt-1 font-medium">
                                    Opportunity: <span className="font-bold text-gray-600">{selectedPosting.gig_type_name}</span>
                                </p>
                            </div>
                            <button onClick={() => setSelectedPosting(null)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {inviteSuccessMsg && (
                            <div className="m-6 mb-0 bg-emerald-50 text-emerald-700 p-4 border border-emerald-100 rounded-2xl text-xs flex items-center gap-2 font-bold animate-pulse">
                                <Check className="w-4 h-4 text-emerald-500 shrink-0" /><span>{inviteSuccessMsg}</span>
                            </div>
                        )}
                        {inviteErrorMsg && (
                            <div className="m-6 mb-0 bg-red-50 text-red-700 p-4 border border-red-100 rounded-2xl text-xs flex items-center gap-2">
                                <Info className="w-4 h-4 text-red-500 shrink-0" /><span><strong>Error:</strong> {inviteErrorMsg}</span>
                            </div>
                        )}
                        {confirmingVolunteer && (
                            <div className="m-6 mb-0 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs space-y-3">
                                <div className="flex gap-2 text-amber-800 font-bold">
                                    <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p>This individual is not eligible for the gig as per the previously specified scope.</p>
                                        <p className="mt-1 text-[11px] font-semibold text-amber-700">Are you sure you want to invite this person?</p>
                                    </div>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={() => setConfirmingVolunteer(null)} className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 font-black uppercase tracking-widest text-[9px] transition-all">Cancel</button>
                                    <button onClick={() => { const v = confirmingVolunteer; setConfirmingVolunteer(null); if (v) handleSendInvitation(v, true); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-black uppercase tracking-widest text-[9px] transition-all">Yes, Invite</button>
                                </div>
                            </div>
                        )}
                        <form onSubmit={handleSearch} className="p-6 pb-0 flex gap-2">
                            <div className="relative flex-1">
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name or email..." className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:border-[#007a8a] font-semibold text-gray-700" disabled={!!inviteSuccessMsg} />
                                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
                            </div>
                            <button type="submit" className="bg-[#003d45] text-white hover:bg-[#00282d] text-xs font-black uppercase tracking-widest px-5 py-2.5 rounded-xl transition-all shadow-sm" disabled={isSearching || !!inviteSuccessMsg}>
                                {isSearching ? "Searching..." : "Search"}
                            </button>
                        </form>
                        <div className="flex-1 overflow-y-auto p-6 min-h-[200px] max-h-[350px]">
                            {searchError && <p className="text-center text-xs text-gray-400 mt-8">{searchError}</p>}
                            {!searchError && searchResults.length === 0 && !isSearching && (
                                <div className="text-center py-12 text-gray-400">
                                    <Users className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                                    <p className="text-xs">Search for active candidates or volunteers to send them an invitation.</p>
                                </div>
                            )}
                            {searchResults.length > 0 && (
                                <div className="space-y-3">
                                    {searchResults.map((v) => (
                                        <div key={v.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-2xl hover:bg-gray-50 transition-colors">
                                            <div className="min-w-0 flex items-center gap-3">
                                                {v.profile_photo_url ? (
                                                    <img src={v.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-[#003d45] text-white text-[10px] font-black flex items-center justify-center uppercase shrink-0">
                                                        {v.first_name?.[0] || ""}{v.last_name?.[0] || ""}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <div className="text-xs font-bold text-[#003d45] truncate">{v.first_name} {v.last_name}</div>
                                                    <div className="text-[10px] text-gray-400 truncate">{v.email}</div>
                                                    {v.job_title && <div className="text-[9px] text-[#007a8a] font-semibold mt-0.5 truncate">{v.job_title} at {v.company || "N/A"}</div>}
                                                </div>
                                            </div>
                                            <button onClick={() => handleInviteButtonClick(v)} disabled={invitingId === v.id || checkingEligibilityId === v.id || !!inviteSuccessMsg} className="bg-[#007a8a]/10 hover:bg-[#007a8a] text-[#007a8a] hover:text-white disabled:bg-gray-100 disabled:text-gray-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shrink-0">
                                                {checkingEligibilityId === v.id ? "Checking..." : invitingId === v.id ? "Inviting..." : "Invite"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirmation Modal ─────────────────────────────────── */}
            {deletingPosting && (
                <div className="fixed inset-0 bg-[#003d45]/45 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white border border-gray-150 shadow-2xl rounded-xl w-full max-w-md overflow-hidden p-6 animate-in zoom-in-95 duration-200 space-y-4">
                        <div className="flex items-center gap-3 text-red-600">
                            <Shield className="w-6 h-6 shrink-0" />
                            <h3 className="text-sm font-black uppercase tracking-wider">Confirm Delete</h3>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed font-semibold">
                            Are you sure you want to permanently delete <strong className="text-[#003d45]">"{deletingPosting.gig_type_name}"</strong>? This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setDeletingPosting(null)} className="px-4 py-2 rounded-xl border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-50 transition-colors">Cancel</button>
                            <button onClick={() => { const p = deletingPosting; setDeletingPosting(null); handleDeleteConfirm(p); }} className="px-4 py-2 rounded-xl bg-[#ae232d] hover:bg-[#8b1c24] text-white text-[10px] font-black uppercase tracking-widest transition-colors shadow-md shadow-red-900/10">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
